import React from 'react';
import { Link, Outlet, NavLink, useNavigate } from 'react-router-dom';
import { User, Car, Flag, CreditCard, LogOut, Loader2 } from 'lucide-react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import { useAuth } from '../../contexts/AuthContext';

const AccountLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#E8651A] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navigation />
        <section className="pt-32 pb-16 px-6 text-center">
          <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-4">SIGN IN REQUIRED</h1>
          <p className="text-[#A19D98] mb-6">Please log in or create an account to access your dashboard.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/login" className="btn-primary">Log In</Link>
            <Link to="/signup" className="btn-secondary">Create Account</Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { to: '/account', label: 'Profile', icon: User, end: true },
    { to: '/account/vehicles', label: 'My Garage', icon: Car },
    { to: '/account/registrations', label: 'Registrations', icon: Flag },
    { to: '/account/payments', label: 'Payments', icon: CreditCard }
  ];

  return (
    <div data-testid="account-layout" className="min-h-screen bg-[#0A0A0A]">
      <Navigation />

      <section className="pt-28 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="font-['Oswald'] text-3xl sm:text-4xl text-[#F2EDE6]">
              HELLO, {user.name?.toUpperCase() || 'RALLY'}.
            </h1>
            <p className="text-[#A19D98] text-sm">{user.email}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
            {/* Sidebar */}
            <aside className="bg-[#141414] border border-[#2A2A2A] p-4 h-fit">
              <nav className="flex lg:flex-col gap-1 overflow-x-auto">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={`account-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-sm font-['Oswald'] tracking-wider whitespace-nowrap transition-colors ${
                        isActive
                          ? 'bg-[#E8651A] text-[#0A0A0A]'
                          : 'text-[#A19D98] hover:text-[#F2EDE6]'
                      }`
                    }
                  >
                    <item.icon size={16} />
                    {item.label}
                  </NavLink>
                ))}
                <button
                  onClick={handleLogout}
                  data-testid="account-logout-btn"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-['Oswald'] tracking-wider text-[#A19D98] hover:text-[#E8651A] mt-2 lg:border-t lg:border-[#2A2A2A] lg:pt-4"
                >
                  <LogOut size={16} />
                  Log Out
                </button>
              </nav>
            </aside>

            {/* Content */}
            <main className="bg-[#141414] border border-[#2A2A2A] p-6 lg:p-8">
              <Outlet />
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AccountLayout;
